/**
 * @fileoverview ClubOS Youth Academy — Player Session Ratings
 * Mobile-first FIFA-card style. Tap a player to open the full rating modal.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import FootballLoader from "components/FootballLoader";

import { academyService } from "api";

// ── Static data ────────────────────────────────────────────────────────────────
const DEFAULT_ATTRIBUTES = [
  { id: "pace",            name: "Pace",            category: "Physical",  icon: "speed" },
  { id: "strength",        name: "Strength",        category: "Physical",  icon: "fitness_center" },
  { id: "stamina",         name: "Stamina",         category: "Physical",  icon: "directions_run" },
  { id: "agility",         name: "Agility",         category: "Physical",  icon: "rotate_right" },
  { id: "ball_control",    name: "Ball Control",    category: "Technical", icon: "sports_soccer" },
  { id: "passing",         name: "Passing",         category: "Technical", icon: "call_split" },
  { id: "shooting",        name: "Shooting",        category: "Technical", icon: "sports" },
  { id: "dribbling",       name: "Dribbling",       category: "Technical", icon: "sync_alt" },
  { id: "positioning",     name: "Positioning",     category: "Mental",    icon: "my_location" },
  { id: "teamwork",        name: "Teamwork",        category: "Mental",    icon: "people" },
  { id: "decision_making", name: "Decision Making", category: "Mental",    icon: "psychology" },
  { id: "attitude",        name: "Attitude",        category: "Mental",    icon: "star" },
];

const SMILEYS = [
  { value: 1, emoji: "😞", label: "Poor",          color: "#ef5350", bg: "rgba(239,83,80,0.18)" },
  { value: 2, emoji: "😐", label: "Below Avg",     color: "#ff9800", bg: "rgba(255,152,0,0.18)" },
  { value: 3, emoji: "🙂", label: "Average",       color: "#ffb300", bg: "rgba(255,179,0,0.18)" },
  { value: 4, emoji: "😊", label: "Good",          color: "#66bb6a", bg: "rgba(102,187,106,0.18)" },
  { value: 5, emoji: "😄", label: "Excellent",     color: "#42a5f5", bg: "rgba(66,165,245,0.18)" },
];

const CAT_META = {
  Physical:  { color: "#42a5f5", label: "PHY" },
  Technical: { color: "#66bb6a", label: "TEC" },
  Mental:    { color: "#ffa726", label: "MEN" },
};

const COMING_SOON = false;

// ── Helpers ────────────────────────────────────────────────────────────────────
function getSmiley(v) { return SMILEYS.find((s) => s.value === Math.round(v)) ?? null; }

function avgOf(attributes, attrRatings, category) {
  const cats = category ? attributes.filter((a) => a.category === category) : attributes;
  const rated = cats.filter((a) => (attrRatings?.[a.id] ?? 0) > 0);
  if (!rated.length) return 0;
  return rated.reduce((s, a) => s + (attrRatings[a.id] ?? 0), 0) / rated.length;
}

function playerScore(r, attributes) {
  if (!r) return 0;
  const attr = avgOf(attributes, r.attributes ?? {}, null);
  if (r.overall > 0 && attr > 0) return (r.overall + attr) / 2;
  return r.overall > 0 ? r.overall : attr;
}

function getCardTier(score) {
  if (score >= 4.5) return {
    name: "Elite",
    gradient: "linear-gradient(145deg,#0d0800 0%,#3d2a05 35%,#7a5a0a 70%,#c8941a 100%)",
    ovrColor: "#ffd700", nameColor: "#fff8dc", borderColor: "#c8941a", glow: "#ffd70040",
  };
  if (score >= 3.5) return {
    name: "Gold",
    gradient: "linear-gradient(145deg,#100d00 0%,#2e2200 35%,#5a4408 70%,#a07a10 100%)",
    ovrColor: "#d4a017", nameColor: "#ffe080", borderColor: "#a07a10", glow: "#d4a01730",
  };
  if (score >= 2.5) return {
    name: "Silver",
    gradient: "linear-gradient(145deg,#080810 0%,#18182e 35%,#303050 70%,#606090 100%)",
    ovrColor: "#b0b0d8", nameColor: "#d0d0f0", borderColor: "#606090", glow: "#b0b0d820",
  };
  return {
    name: "Bronze",
    gradient: "linear-gradient(145deg,#100500 0%,#2a1000 35%,#4a2010 70%,#7a4020 100%)",
    ovrColor: "#d08840", nameColor: "#f0c880", borderColor: "#7a4020", glow: "#d0884020",
  };
}

const UNRATED_TIER = {
  gradient: "linear-gradient(145deg,#10101a 0%,#1a1a28 100%)",
  ovrColor: "rgba(255,255,255,0.18)", nameColor: "rgba(255,255,255,0.18)", borderColor: "#2a2a3a", glow: "transparent",
};

// ── SVG Radar (triangle: PHY top, TEC bottom-right, MEN bottom-left) ──────────
function RadarChart({ physical, technical, mental, size = 96 }) {
  const C = size / 2;
  const R = size * 0.35;
  const angles = [-90, 30, 150].map((d) => (d * Math.PI) / 180);
  const vals = [physical, technical, mental];

  const maxPts = angles.map((a) => ({ x: C + R * Math.cos(a), y: C + R * Math.sin(a) }));
  const valPts = vals.map((v, i) => {
    const r = (Math.min(v, 5) / 5) * R;
    return { x: C + r * Math.cos(angles[i]), y: C + r * Math.sin(angles[i]) };
  });
  const grids = [0.33, 0.67, 1].map((s) =>
    angles.map((a) => ({ x: C + R * s * Math.cos(a), y: C + R * s * Math.sin(a) }))
  );
  const labels = ["PHY", "TEC", "MEN"];
  const lDist = R * 1.38;

  return (
    <svg width={size} height={size} style={{ display: "block", overflow: "visible" }}>
      {grids.map((pts, gi) => (
        <polygon key={gi}
          points={pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ")}
          fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.8" />
      ))}
      {maxPts.map((p, i) => (
        <line key={i} x1={C} y1={C} x2={p.x.toFixed(1)} y2={p.y.toFixed(1)}
          stroke="rgba(255,255,255,0.1)" strokeWidth="0.8" />
      ))}
      <polygon
        points={valPts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ")}
        fill="rgba(255,215,0,0.15)" stroke="#ffd700" strokeWidth="1.5" />
      {valPts.map((p, i) => vals[i] > 0 && (
        <circle key={i} cx={p.x.toFixed(1)} cy={p.y.toFixed(1)} r="2.5"
          fill="#ffd700" stroke="rgba(255,255,255,0.5)" strokeWidth="0.5" />
      ))}
      {angles.map((a, i) => {
        const x = C + lDist * Math.cos(a);
        const y = C + lDist * Math.sin(a);
        return (
          <text key={i} x={x.toFixed(1)} y={y.toFixed(1)}
            textAnchor="middle" dominantBaseline="middle"
            fill="rgba(255,255,255,0.5)" fontSize="7" fontWeight="700" fontFamily="monospace">
            {labels[i]}
          </text>
        );
      })}
    </svg>
  );
}
RadarChart.propTypes = { physical: PropTypes.number, technical: PropTypes.number, mental: PropTypes.number, size: PropTypes.number };
RadarChart.defaultProps = { physical: 0, technical: 0, mental: 0, size: 96 };

// ── Touch-friendly smiley selector ───────────────────────────────────────────
function SmileyRating({ value, onChange, size = 56 }) {
  return (
    <SoftBox display="flex" gap={1} justifyContent="center" flexWrap="nowrap">
      {SMILEYS.map((s) => {
        const active = value === s.value;
        return (
          <SoftBox
            key={s.value}
            onClick={() => onChange(active ? 0 : s.value)}
            sx={{
              width: size, height: size, borderRadius: 2,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              cursor: "pointer", gap: 0.25,
              backgroundColor: active ? s.bg : "rgba(255,255,255,0.04)",
              border: `2px solid ${active ? s.color : "rgba(255,255,255,0.07)"}`,
              transition: "all 0.14s ease",
              transform: active ? "scale(1.1)" : "scale(1)",
              filter: active ? "none" : "grayscale(65%) opacity(0.5)",
              userSelect: "none",
              WebkitTapHighlightColor: "transparent",
              "&:active": { transform: "scale(0.93)" },
              "&:hover": { filter: "none", backgroundColor: s.bg, transform: "scale(1.06)" },
            }}
          >
            <SoftTypography sx={{ fontSize: size >= 52 ? "1.7rem" : "1.2rem", lineHeight: 1 }}>
              {s.emoji}
            </SoftTypography>
            {size >= 52 && (
              <SoftTypography sx={{ fontSize: "0.42rem", fontWeight: 700, color: active ? s.color : "rgba(255,255,255,0.25)", letterSpacing: 0.3, lineHeight: 1, textTransform: "uppercase" }}>
                {s.label}
              </SoftTypography>
            )}
          </SoftBox>
        );
      })}
    </SoftBox>
  );
}
SmileyRating.propTypes = { value: PropTypes.number.isRequired, onChange: PropTypes.func.isRequired, size: PropTypes.number };

// ── Player list row ────────────────────────────────────────────────────────────
function PlayerRow({ player, rating, attributes, onSelect, onToggleAttendance }) {
  const score = playerScore(rating, attributes);
  const tier = score > 0 ? getCardTier(score) : UNRATED_TIER;
  const isPresent = rating?.attendanceMarked !== false;
  const initials = (player.fullName ?? "")
    .split(" ").map((n) => n[0] ?? "").join("").slice(0, 2).toUpperCase();

  const phyAvg = avgOf(attributes, rating?.attributes, "Physical");
  const tecAvg = avgOf(attributes, rating?.attributes, "Technical");
  const menAvg = avgOf(attributes, rating?.attributes, "Mental");
  const isRated = score > 0;

  return (
    <SoftBox
      sx={{
        display: "flex", alignItems: "center", gap: 1.5, p: 1.5, mb: 1,
        borderRadius: 2.5,
        backgroundColor: "#fff",
        border: "1.5px solid",
        borderColor: isPresent ? "#f0f0f0" : "rgba(244,67,54,0.15)",
        cursor: "pointer",
        transition: "box-shadow 0.15s, transform 0.1s",
        WebkitTapHighlightColor: "transparent",
        "&:active": { transform: "scale(0.985)" },
        "&:hover": { boxShadow: "0 4px 18px rgba(0,0,0,0.09)", borderColor: "#d0d8ff" },
      }}
    >
      {/* FIFA mini card */}
      <SoftBox
        onClick={(e) => { e.stopPropagation(); onSelect(); }}
        sx={{
          width: 52, height: 70, borderRadius: 1.5, flexShrink: 0,
          background: tier.gradient,
          border: `1.5px solid ${tier.borderColor}`,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          gap: 0.5,
          boxShadow: isRated ? `0 3px 10px ${tier.glow}` : "none",
        }}
      >
        {/* Initials avatar */}
        <SoftBox sx={{
          width: 28, height: 28, borderRadius: "50%",
          border: `1.5px solid ${tier.borderColor}`,
          backgroundColor: "rgba(255,255,255,0.07)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <SoftTypography sx={{ fontSize: "0.65rem", fontWeight: 900, color: tier.ovrColor, lineHeight: 1 }}>
            {initials}
          </SoftTypography>
        </SoftBox>
        <SoftTypography sx={{ fontSize: isRated ? "1.1rem" : "0.75rem", fontWeight: 900, color: tier.ovrColor, lineHeight: 1, fontFamily: "monospace" }}>
          {isRated ? score.toFixed(1) : "—"}
        </SoftTypography>
        <SoftTypography sx={{ fontSize: "0.38rem", fontWeight: 700, color: tier.nameColor, letterSpacing: 0.5, textTransform: "uppercase" }}>
          {isRated ? tier.name : "RATE"}
        </SoftTypography>
      </SoftBox>

      {/* Player info */}
      <SoftBox flex={1} minWidth={0} onClick={onSelect}>
        <SoftTypography fontWeight="bold" sx={{ fontSize: "0.9rem", lineHeight: 1.2, color: "#1a1a2e", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {player.fullName}
        </SoftTypography>
        <SoftTypography sx={{ fontSize: "0.62rem", color: "#999", fontFamily: "monospace" }}>
          {player.playerCode}
        </SoftTypography>

        {isRated ? (
          <SoftBox display="flex" gap={1.5} mt={0.75}>
            {[
              { label: "PHY", val: phyAvg, color: CAT_META.Physical.color },
              { label: "TEC", val: tecAvg, color: CAT_META.Technical.color },
              { label: "MEN", val: menAvg, color: CAT_META.Mental.color },
            ].map(({ label, val, color }) => (
              <SoftBox key={label} display="flex" alignItems="center" gap={0.5}>
                <SoftTypography sx={{ fontSize: "0.52rem", fontWeight: 700, color: "#bbb" }}>{label}</SoftTypography>
                <SoftBox sx={{ width: 28, height: 4, borderRadius: 2, backgroundColor: "#eee", overflow: "hidden" }}>
                  <SoftBox sx={{ height: "100%", width: `${val > 0 ? (val / 5) * 100 : 0}%`, backgroundColor: color, borderRadius: 2 }} />
                </SoftBox>
                <SoftTypography sx={{ fontSize: "0.55rem", fontWeight: 700, color }}>{val > 0 ? val.toFixed(1) : "—"}</SoftTypography>
              </SoftBox>
            ))}
          </SoftBox>
        ) : (
          <SoftTypography sx={{ fontSize: "0.62rem", color: "#bbb", mt: 0.5 }}>Tap to rate</SoftTypography>
        )}
      </SoftBox>

      {/* Right controls */}
      <SoftBox display="flex" flexDirection="column" alignItems="flex-end" gap={0.75} flexShrink={0}>
        <Chip
          label={isPresent ? "Present" : "Absent"}
          size="small"
          icon={<Icon sx={{ fontSize: "0.75rem !important" }}>{isPresent ? "check_circle" : "cancel"}</Icon>}
          onClick={(e) => { e.stopPropagation(); onToggleAttendance(); }}
          sx={{
            height: 30, fontSize: "0.65rem", fontWeight: 700, cursor: "pointer",
            backgroundColor: isPresent ? "rgba(76,175,80,0.1)" : "rgba(244,67,54,0.08)",
            color: isPresent ? "#2e7d32" : "#c62828",
            border: `1px solid ${isPresent ? "rgba(76,175,80,0.3)" : "rgba(244,67,54,0.25)"}`,
            "& .MuiChip-icon": { color: isPresent ? "#2e7d32" : "#c62828" },
          }}
        />
        <SoftBox
          onClick={onSelect}
          sx={{
            width: 32, height: 28, borderRadius: 1, cursor: "pointer",
            backgroundColor: "#f0f4ff", display: "flex", alignItems: "center", justifyContent: "center",
            "&:hover": { backgroundColor: "#e0e8ff" },
          }}
        >
          <Icon sx={{ fontSize: "1rem !important", color: "#3f51b5" }}>
            {isRated ? "edit" : "star_rate"}
          </Icon>
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}
PlayerRow.propTypes = {
  player: PropTypes.object.isRequired,
  rating: PropTypes.object,
  attributes: PropTypes.array.isRequired,
  onSelect: PropTypes.func.isRequired,
  onToggleAttendance: PropTypes.func.isRequired,
};

// ── Player Rating Modal ────────────────────────────────────────────────────────
function PlayerRatingModal({ open, onClose, player, rating, attributes, onChange, onOverallChange, onToggleAttendance, onSave, saving }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  if (!player) return null;

  const r = rating ?? { overall: 0, attendanceMarked: true, attributes: {} };
  const isPresent = r.attendanceMarked !== false;
  const score = playerScore(r, attributes);
  const tier = score > 0 ? getCardTier(score) : UNRATED_TIER;
  const phyAvg = avgOf(attributes, r.attributes, "Physical");
  const tecAvg = avgOf(attributes, r.attributes, "Technical");
  const menAvg = avgOf(attributes, r.attributes, "Mental");
  const categories = [...new Set(attributes.map((a) => a.category))];
  const initials = (player.fullName ?? "").split(" ").map((n) => n[0] ?? "").join("").slice(0, 2).toUpperCase();
  const lastName = (player.fullName ?? "").split(" ").slice(-1)[0]?.toUpperCase() ?? "";
  const overallSmiley = r.overall > 0 ? getSmiley(r.overall) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          overflow: "hidden",
          maxHeight: fullScreen ? "100dvh" : "92vh",
          background: "#0c0e16",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* ── FIFA Card Header ────────────────────────────────────────────── */}
      <SoftBox sx={{ flexShrink: 0, background: tier.gradient, position: "relative", overflow: "hidden" }}>
        {/* Dot-grid pattern */}
        <SoftBox sx={{
          position: "absolute", inset: 0, pointerEvents: "none", opacity: 0.045,
          backgroundImage: "radial-gradient(circle,#fff 1px,transparent 1px)",
          backgroundSize: "18px 18px",
        }} />

        {/* Close */}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute", top: 10, right: 10, zIndex: 10,
            color: "rgba(255,255,255,0.45)",
            "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.1)" },
          }}
        >
          <Icon>close</Icon>
        </IconButton>

        {/* Main card row */}
        <SoftBox display="flex" alignItems="center" gap={2} px={2.5} pt={3} pb={1.5}>
          {/* OVR */}
          <SoftBox textAlign="center" sx={{ minWidth: 58 }}>
            <SoftTypography sx={{ fontSize: "2.8rem", fontWeight: 900, lineHeight: 1, fontFamily: "monospace", color: tier.ovrColor }}>
              {score > 0 ? score.toFixed(1) : "—"}
            </SoftTypography>
            <SoftTypography sx={{ fontSize: "0.48rem", fontWeight: 800, letterSpacing: 2, color: tier.nameColor, textTransform: "uppercase" }}>
              {score > 0 ? tier.name : "UNRATED"}
            </SoftTypography>
          </SoftBox>

          {/* Avatar + Name */}
          <SoftBox flex={1} textAlign="center">
            <SoftBox sx={{
              width: 64, height: 64, borderRadius: "50%", mx: "auto", mb: 1,
              background: `radial-gradient(circle at 35% 30%, rgba(255,255,255,0.22), transparent 70%)`,
              border: `2.5px solid ${tier.borderColor}`,
              backgroundColor: "rgba(255,255,255,0.06)",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: score > 0 ? `0 0 24px ${tier.glow}` : "none",
            }}>
              <SoftTypography sx={{ fontSize: "1.4rem", fontWeight: 900, color: tier.ovrColor }}>{initials}</SoftTypography>
            </SoftBox>
            <SoftTypography sx={{ fontSize: "1rem", fontWeight: 900, color: tier.nameColor, textTransform: "uppercase", letterSpacing: 2, lineHeight: 1 }}>
              {lastName}
            </SoftTypography>
            <SoftTypography sx={{ fontSize: "0.58rem", color: "rgba(255,255,255,0.3)", fontFamily: "monospace", mt: 0.25 }}>
              {player.playerCode}
            </SoftTypography>
          </SoftBox>

          {/* Radar chart */}
          <SoftBox sx={{ width: 96, flexShrink: 0 }}>
            <RadarChart physical={phyAvg} technical={tecAvg} mental={menAvg} size={96} />
          </SoftBox>
        </SoftBox>

        {/* Stat strip */}
        <SoftBox display="flex" justifyContent="space-around" px={2} pb={1.5} pt={0.5}
          sx={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}>
          {[
            { label: "PHY", val: phyAvg, color: CAT_META.Physical.color },
            { label: "OVR", val: r.overall > 0 ? r.overall : null, color: tier.ovrColor },
            { label: "TEC", val: tecAvg, color: CAT_META.Technical.color },
            { label: "MEN", val: menAvg, color: CAT_META.Mental.color },
          ].map(({ label, val, color }) => (
            <SoftBox key={label} textAlign="center">
              <SoftTypography sx={{ fontSize: val ? "1.05rem" : "0.8rem", fontWeight: 900, lineHeight: 1, color: val ? color : "rgba(255,255,255,0.12)" }}>
                {val ? val.toFixed(1) : "—"}
              </SoftTypography>
              <SoftTypography sx={{ fontSize: "0.48rem", fontWeight: 700, color: "rgba(255,255,255,0.35)", letterSpacing: 1 }}>
                {label}
              </SoftTypography>
            </SoftBox>
          ))}
        </SoftBox>

        {/* Attendance toggle */}
        <SoftBox display="flex" justifyContent="center" pb={2.5}>
          <SoftBox
            onClick={onToggleAttendance}
            sx={{
              display: "flex", alignItems: "center", gap: 1, px: 3.5, py: 1.25,
              borderRadius: 10, cursor: "pointer",
              backgroundColor: isPresent ? "rgba(76,175,80,0.18)" : "rgba(244,67,54,0.15)",
              border: `1.5px solid ${isPresent ? "rgba(76,175,80,0.45)" : "rgba(244,67,54,0.4)"}`,
              transition: "all 0.14s ease",
              userSelect: "none", WebkitTapHighlightColor: "transparent",
              "&:hover": { backgroundColor: isPresent ? "rgba(76,175,80,0.28)" : "rgba(244,67,54,0.25)" },
              "&:active": { transform: "scale(0.96)" },
            }}
          >
            <Icon sx={{ fontSize: "1.1rem !important", color: isPresent ? "#66bb6a" : "#ef5350" }}>
              {isPresent ? "check_circle" : "cancel"}
            </Icon>
            <SoftTypography sx={{ fontSize: "0.8rem", fontWeight: 800, letterSpacing: 2, color: isPresent ? "#66bb6a" : "#ef5350" }}>
              {isPresent ? "PRESENT" : "ABSENT"}
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      </SoftBox>

      {/* ── Scrollable ratings body ─────────────────────────────────────── */}
      <DialogContent
        sx={{
          p: 0, backgroundColor: "#0c0e16", overflowY: "auto", flex: 1,
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(255,255,255,0.1)", borderRadius: 2 },
        }}
      >
        {/* Overall day rating */}
        <SoftBox px={2.5} py={2.5} sx={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
            <SoftBox display="flex" alignItems="center" gap={1}>
              <SoftBox sx={{ width: 8, height: 20, borderRadius: 1, backgroundColor: "#ffd700" }} />
              <SoftTypography sx={{ fontSize: "0.65rem", fontWeight: 800, letterSpacing: 2, color: "#ffd700" }}>
                OVERALL DAY RATING
              </SoftTypography>
            </SoftBox>
            {overallSmiley && (
              <SoftTypography sx={{ fontSize: "0.72rem", fontWeight: 700, color: overallSmiley.color }}>
                {overallSmiley.emoji} {overallSmiley.label}
              </SoftTypography>
            )}
          </SoftBox>
          <SmileyRating value={r.overall ?? 0} onChange={onOverallChange} size={56} />
        </SoftBox>

        {/* Attributes by category */}
        {categories.map((cat) => {
          const meta = CAT_META[cat] ?? CAT_META.Mental;
          const catAttrs = attributes.filter((a) => a.category === cat);
          const catAvg = avgOf(attributes, r.attributes, cat);

          return (
            <SoftBox key={cat} px={2.5} py={2} sx={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
              {/* Category header */}
              <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <SoftBox display="flex" alignItems="center" gap={1}>
                  <SoftBox sx={{ width: 8, height: 22, borderRadius: 1, backgroundColor: meta.color, flexShrink: 0 }} />
                  <SoftTypography sx={{ fontSize: "0.65rem", fontWeight: 800, letterSpacing: 2, color: meta.color }}>
                    {cat.toUpperCase()}
                  </SoftTypography>
                </SoftBox>
                {catAvg > 0 && (
                  <SoftBox display="flex" alignItems="center" gap={0.75}>
                    <SoftBox sx={{ width: 52, height: 5, borderRadius: 3, backgroundColor: "rgba(255,255,255,0.07)", overflow: "hidden" }}>
                      <SoftBox sx={{ height: "100%", width: `${(catAvg / 5) * 100}%`, backgroundColor: meta.color, borderRadius: 3 }} />
                    </SoftBox>
                    <SoftTypography sx={{ fontSize: "0.7rem", fontWeight: 700, color: meta.color }}>{catAvg.toFixed(1)}</SoftTypography>
                  </SoftBox>
                )}
              </SoftBox>

              {catAttrs.map((attr) => {
                const attrVal = r.attributes?.[attr.id] ?? 0;
                const attrSmiley = attrVal > 0 ? getSmiley(attrVal) : null;
                return (
                  <SoftBox key={attr.id} mb={2.5}>
                    <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <SoftBox display="flex" alignItems="center" gap={1}>
                        <Icon sx={{ fontSize: "0.95rem !important", color: meta.color }}>{attr.icon}</Icon>
                        <SoftTypography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "rgba(255,255,255,0.78)" }}>
                          {attr.name}
                        </SoftTypography>
                      </SoftBox>
                      {attrSmiley && (
                        <SoftTypography sx={{ fontSize: "0.7rem", fontWeight: 700, color: attrSmiley.color }}>
                          {attrSmiley.emoji} {attrSmiley.label}
                        </SoftTypography>
                      )}
                    </SoftBox>
                    <SmileyRating value={attrVal} onChange={(v) => onChange(attr.id, v)} size={52} />
                  </SoftBox>
                );
              })}
            </SoftBox>
          );
        })}
        <SoftBox sx={{ height: 12 }} />
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ p: 2, backgroundColor: "#0c0e16", borderTop: "1px solid rgba(255,255,255,0.07)", gap: 1, flexShrink: 0 }}>
        <SoftButton
          variant="outlined" color="secondary" onClick={onClose}
          sx={{ flex: 1, color: "rgba(255,255,255,0.45)", borderColor: "rgba(255,255,255,0.12)" }}
        >
          Done
        </SoftButton>
        <SoftButton
          variant="gradient" color="success"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Icon>save</Icon>}
          onClick={onSave} disabled={saving}
          sx={{ flex: 2 }}
        >
          {saving ? "Saving…" : "Save Session"}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}
PlayerRatingModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  player: PropTypes.object,
  rating: PropTypes.object,
  attributes: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  onOverallChange: PropTypes.func.isRequired,
  onToggleAttendance: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
};

// ── Attribute Config Dialog ────────────────────────────────────────────────────
function AttributeConfigDialog({ open, onClose, attributes, onSave }) {
  const [draft, setDraft] = useState(attributes);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setDraft(attributes); }, [open, attributes]);

  const addAttr = () => setDraft((p) => [...p, { id: `attr_${Date.now()}`, name: "", category: "Technical", icon: "sports_soccer" }]);
  const removeAttr = (id) => setDraft((p) => p.filter((a) => a.id !== id));
  const updateAttr = (id, field, value) => setDraft((p) => p.map((a) => a.id === id ? { ...a, [field]: value } : a));

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(draft.filter((a) => a.name.trim())); onClose(); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: "85vh" } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>Configure Rating Attributes</DialogTitle>
      <DialogContent dividers>
        {draft.map((attr) => (
          <SoftBox key={attr.id} display="flex" alignItems="center" gap={1} mb={1}>
            <TextField size="small" label="Name" value={attr.name}
              onChange={(e) => updateAttr(attr.id, "name", e.target.value)} sx={{ flex: "0 0 170px" }} />
            <TextField select size="small" label="Category" value={attr.category}
              onChange={(e) => updateAttr(attr.id, "category", e.target.value)} sx={{ flex: "0 0 120px" }}>
              {["Physical", "Technical", "Mental"].map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Icon (MUI)" value={attr.icon}
              onChange={(e) => updateAttr(attr.id, "icon", e.target.value)} sx={{ flex: "0 0 120px" }} />
            <Icon sx={{ fontSize: "1.2rem", color: "#666" }}>{attr.icon}</Icon>
            <SoftButton variant="text" color="error" iconOnly size="small" onClick={() => removeAttr(attr.id)}>
              <Icon>delete</Icon>
            </SoftButton>
          </SoftBox>
        ))}
        <SoftButton variant="outlined" color="primary" size="small" startIcon={<Icon>add</Icon>} onClick={addAttr} sx={{ mt: 1 }}>
          Add Attribute
        </SoftButton>
      </DialogContent>
      <DialogActions>
        <SoftButton variant="text" color="secondary" onClick={onClose}>Cancel</SoftButton>
        <SoftButton variant="gradient" color="primary" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save Attributes"}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}
AttributeConfigDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  attributes: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
};

// ── Coming Soon screen ─────────────────────────────────────────────────────────
function ComingSoonPage() {
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox
        py={4}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="75vh"
        sx={{
          overflow: "hidden",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background: "radial-gradient(ellipse at 50% 10%, rgba(200,148,26,0.09) 0%, transparent 68%)",
            pointerEvents: "none",
          },
        }}
      >
        {/* Floating FIFA-style card */}
        <SoftBox
          mb={4.5}
          sx={{
            "@keyframes float": {
              "0%,100%": { transform: "translateY(0px) rotate(-3deg)", filter: "drop-shadow(0 20px 40px rgba(200,148,26,0.35))" },
              "50%":     { transform: "translateY(-22px) rotate(3deg)",  filter: "drop-shadow(0 38px 60px rgba(200,148,26,0.62))" },
            },
            animation: "float 3.8s ease-in-out infinite",
            cursor: "default",
            userSelect: "none",
          }}
        >
          <SoftBox
            sx={{
              width: 148,
              height: 210,
              borderRadius: "12px",
              background: "linear-gradient(145deg,#0d0800 0%,#3d2000 22%,#c8941a 54%,#ffd700 70%,#c8941a 84%,#0d0800 100%)",
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              overflow: "hidden",
              boxShadow: "0 8px 36px rgba(200,148,26,0.45), inset 0 1px 0 rgba(255,255,255,0.14)",
              border: "1px solid rgba(255,215,0,0.3)",
              "@keyframes cardShine": {
                "0%":      { transform: "translateX(-180%) rotate(30deg)" },
                "40%,100%": { transform: "translateX(230%) rotate(30deg)" },
              },
              "&::after": {
                content: '""',
                position: "absolute",
                top: "-50%",
                left: 0,
                width: "40%",
                height: "200%",
                background: "linear-gradient(90deg,transparent,rgba(255,255,255,0.18),transparent)",
                animation: "cardShine 3s ease-in-out infinite",
              },
            }}
          >
            {/* Dot-grid texture */}
            <SoftBox sx={{
              position: "absolute", inset: 0, opacity: 0.07,
              backgroundImage: "radial-gradient(circle,#fff 1px,transparent 1px)",
              backgroundSize: "8px 8px",
            }} />

            {/* OVR block */}
            <SoftBox display="flex" flexDirection="column" alignItems="center" pt={2.5} sx={{ zIndex: 1 }}>
              <SoftTypography sx={{ fontSize: "0.48rem", letterSpacing: "0.18em", color: "#ffd700", fontWeight: 800, lineHeight: 1, fontFamily: "monospace" }}>
                ELITE
              </SoftTypography>
              <SoftTypography sx={{ fontSize: "2.2rem", fontWeight: 900, color: "#ffd700", lineHeight: 1.1, fontFamily: "monospace", textShadow: "0 0 14px rgba(255,215,0,0.65)" }}>
                99
              </SoftTypography>
              <SoftTypography sx={{ fontSize: "0.36rem", letterSpacing: "0.12em", color: "rgba(255,215,0,0.8)", fontWeight: 700, fontFamily: "monospace" }}>
                OVR
              </SoftTypography>
            </SoftBox>

            {/* Football icon */}
            <SoftBox display="flex" justifyContent="center" pt={0.75} sx={{ zIndex: 1 }}>
              <Icon sx={{ fontSize: "3.2rem !important", color: "rgba(255,255,255,0.92)", textShadow: "0 0 18px rgba(255,215,0,0.5)" }}>
                sports_soccer
              </Icon>
            </SoftBox>

            {/* Stat strip */}
            <SoftBox display="flex" gap={1.5} pt={1} sx={{ zIndex: 1 }}>
              {[["PHY","—"],["TEC","—"],["MEN","—"]].map(([lbl, val]) => (
                <SoftBox key={lbl} textAlign="center">
                  <SoftTypography sx={{ fontSize: "0.58rem", color: "#ffd700", fontWeight: 800, fontFamily: "monospace", lineHeight: 1 }}>{val}</SoftTypography>
                  <SoftTypography sx={{ fontSize: "0.36rem", color: "rgba(255,215,0,0.7)", fontFamily: "monospace" }}>{lbl}</SoftTypography>
                </SoftBox>
              ))}
            </SoftBox>

            {/* Name strip */}
            <SoftBox sx={{
              position: "absolute", bottom: 0, left: 0, right: 0,
              backgroundColor: "rgba(0,0,0,0.55)",
              py: 0.75, textAlign: "center",
              borderTop: "1px solid rgba(255,215,0,0.2)",
              zIndex: 1,
            }}>
              <SoftTypography sx={{ fontSize: "0.4rem", letterSpacing: "0.2em", color: "#ffd700", fontWeight: 800, fontFamily: "monospace" }}>
                YOUR PLAYER
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        </SoftBox>

        {/* Pulsing badge */}
        <SoftBox
          mb={1.5}
          sx={{
            px: 1.5, py: 0.5, borderRadius: "20px",
            background: "linear-gradient(90deg,rgba(200,148,26,0.14),rgba(255,215,0,0.14))",
            border: "1px solid rgba(255,215,0,0.3)",
            "@keyframes badgePulse": {
              "0%,100%": { opacity: 1, boxShadow: "0 0 0 0 rgba(255,215,0,0.3)" },
              "50%":     { opacity: 0.8, boxShadow: "0 0 0 7px rgba(255,215,0,0)" },
            },
            animation: "badgePulse 2.1s ease-in-out infinite",
          }}
        >
          <SoftTypography sx={{ fontSize: "0.58rem", letterSpacing: "0.22em", fontWeight: 800, color: "#b8841a", fontFamily: "monospace" }}>
            ⚡ NEW FEATURE IN PROGRESS
          </SoftTypography>
        </SoftBox>

        {/* Shimmer headline */}
        <SoftTypography
          variant="h2"
          fontWeight="bold"
          textAlign="center"
          sx={{
            fontSize: { xs: "2rem", sm: "2.7rem" },
            letterSpacing: "-0.02em",
            lineHeight: 1.15,
            mb: 0.75,
            background: "linear-gradient(120deg,#a06010 0%,#c8941a 25%,#ffd700 48%,#c8941a 72%,#a06010 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            backgroundSize: "220% auto",
            "@keyframes shimmer": {
              "0%":   { backgroundPosition: "0% 50%" },
              "100%": { backgroundPosition: "220% 50%" },
            },
            animation: "shimmer 3.2s linear infinite",
          }}
        >
          Player Ratings
        </SoftTypography>

        <SoftTypography variant="h6" textAlign="center" sx={{ color: "rgba(0,0,0,0.48)", fontWeight: 400, mb: 0.5 }}>
          Card style &nbsp;·&nbsp; Radar charts &nbsp;·&nbsp; Mobile-first
        </SoftTypography>

        <SoftTypography
          variant="body2"
          textAlign="center"
          fontStyle="italic"
          sx={{ color: "rgba(0,0,0,0.35)", mb: 4, maxWidth: 330 }}
        >
          Rate your squad like a pro — smiley ratings, attendance tracking &amp; tier cards for every player
        </SoftTypography>

        {/* Bouncing dots */}
        <SoftBox display="flex" gap={1} alignItems="center">
          {[0,1,2,3].map((i) => (
            <SoftBox key={i} sx={{
              width: 9, height: 9, borderRadius: "50%",
              backgroundColor: "#c8941a",
              "@keyframes dotBounce": {
                "0%,80%,100%": { transform: "scale(0.55)", opacity: 0.35 },
                "40%":          { transform: "scale(1.25)", opacity: 1 },
              },
              animation: `dotBounce 1.4s ease-in-out ${i * 0.18}s infinite`,
            }} />
          ))}
        </SoftBox>

        <SoftTypography variant="caption" sx={{ mt: 1.5, color: "rgba(0,0,0,0.28)", letterSpacing: "0.06em" }}>
          Coming soon
        </SoftTypography>
      </SoftBox>
      <Footer company={{ href: "#", name: "Mohammedan SC" }} links={[]} />
    </DashboardLayout>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
function AcademyRatingsPage() {
  const today = new Date().toISOString().slice(0, 10);

  const [batches, setBatches]       = useState([]);
  const [players, setPlayers]       = useState([]);
  const [attributes, setAttributes] = useState(DEFAULT_ATTRIBUTES);
  const [batchId, setBatchId]       = useState("");
  const [sessionDate, setSessionDate] = useState(today);
  const [loading, setLoading]       = useState(false);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [saving, setSaving]         = useState(false);
  const [saved, setSaved]           = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [snack, setSnack]           = useState({ open: false, msg: "", sev: "success" });

  const [ratings, setRatings] = useState({});

  const showSnack = (msg, sev = "success") => setSnack({ open: true, msg, sev });

  useEffect(() => {
    setLoading(true);
    Promise.all([
      academyService.getBatches({ status: "Active" }),
      academyService.getConfigSettings().catch(() => []),
    ]).then(([b, cfg]) => {
      setBatches(Array.isArray(b) ? b : []);
      if (Array.isArray(cfg)) {
        const rAttr = cfg.find((c) => c.key === "RatingAttributes");
        if (rAttr?.value) { try { setAttributes(JSON.parse(rAttr.value)); } catch { /* use defaults */ } }
      }
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!batchId) { setPlayers([]); return; }
    setPlayersLoading(true);
    academyService.getPlayers()
      .then((all) => {
        const inBatch = (Array.isArray(all) ? all : []).filter((p) => p.batchId === batchId && p.status !== "Inactive");
        setPlayers(inBatch);
        setRatings((prev) => {
          const next = { ...prev };
          inBatch.forEach((p) => { if (!next[p.id]) next[p.id] = { overall: 0, attendanceMarked: true, attributes: {} }; });
          return next;
        });
      })
      .finally(() => setPlayersLoading(false));
  }, [batchId]);

  useEffect(() => {
    if (!batchId || !sessionDate) return;
    academyService.getPlayerRatingSession(batchId, sessionDate)
      .then((session) => {
        if (!session?.playerRatings) return;
        const next = {};
        session.playerRatings.forEach((pr) => {
          const attrMap = {};
          (pr.attributes ?? []).forEach((a) => { attrMap[a.id] = a.rating; });
          next[pr.playerId] = { overall: pr.overallRating ?? 0, attendanceMarked: pr.attendanceMarked !== false, attributes: attrMap };
        });
        setRatings((prev) => ({ ...prev, ...next }));
        setSaved(true);
      })
      .catch(() => {});
  }, [batchId, sessionDate]);

  const updateRating = useCallback((playerId, attrId, value) => {
    setRatings((prev) => ({
      ...prev,
      [playerId]: { ...prev[playerId], attributes: { ...(prev[playerId]?.attributes ?? {}), [attrId]: value } },
    }));
    setSaved(false);
  }, []);

  const updateOverall = useCallback((playerId, value) => {
    setRatings((prev) => ({ ...prev, [playerId]: { ...prev[playerId], overall: value } }));
    setSaved(false);
  }, []);

  const toggleAttendance = useCallback((playerId) => {
    setRatings((prev) => ({
      ...prev,
      [playerId]: { ...prev[playerId], attendanceMarked: !(prev[playerId]?.attendanceMarked ?? true) },
    }));
    setSaved(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (!batchId) { showSnack("Select a batch first.", "warning"); return; }
    setSaving(true);
    try {
      const batch = batches.find((b) => b.id === batchId);
      await academyService.savePlayerRatingSession({
        batchId, batchName: batch?.name, sessionDate,
        playerRatings: players.map((p) => {
          const r = ratings[p.id] ?? {};
          return {
            playerId: p.id, playerName: p.fullName, playerCode: p.playerCode,
            attendanceMarked: r.attendanceMarked !== false,
            overallRating: r.overall ?? 0,
            attributes: attributes.map((a) => ({ id: a.id, name: a.name, rating: r.attributes?.[a.id] ?? 0 })),
          };
        }),
      });
      setSaved(true);
      showSnack("Session saved · attendance marked.", "success");
    } catch (e) {
      showSnack(e?.errors?.detail ?? e?.message ?? "Save failed.", "error");
    } finally { setSaving(false); }
  }, [batchId, batches, sessionDate, players, ratings, attributes]);

  const handleSaveAttributes = async (newAttrs) => {
    await academyService.saveConfigSettings({
      settings: [{ key: "RatingAttributes", value: JSON.stringify(newAttrs), description: "Player rating attribute definitions (JSON)" }],
    });
    setAttributes(newAttrs);
    showSnack("Attributes saved.", "success");
  };

  const ratedCount   = useMemo(() => players.filter((p) => { const r = ratings[p.id]; return r && (r.overall > 0 || Object.values(r.attributes ?? {}).some((v) => v > 0)); }).length, [players, ratings]);
  const presentCount = useMemo(() => players.filter((p) => ratings[p.id]?.attendanceMarked !== false).length, [players, ratings]);
  const batchAvg     = useMemo(() => {
    const scores = players.map((p) => playerScore(ratings[p.id], attributes)).filter((v) => v > 0);
    return scores.length ? scores.reduce((s, v) => s + v, 0) / scores.length : 0;
  }, [players, ratings, attributes]);

  if (loading) return (
    <DashboardLayout><DashboardNavbar />
      <SoftBox py={3} display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <FootballLoader label="Loading…" minHeight={200} />
      </SoftBox>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>

        {/* Page header */}
        <SoftBox mb={2.5} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">Player Ratings</SoftTypography>
            <SoftTypography variant="body2" color="text">Rate players · attendance auto-marked on save</SoftTypography>
          </SoftBox>
          <SoftBox display="flex" gap={1} flexWrap="wrap" alignItems="center">
            {saved && (
              <Chip icon={<Icon sx={{ fontSize: "0.85rem !important" }}>check_circle</Icon>}
                label="Saved" size="small"
                sx={{ backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: 700 }} />
            )}
            <SoftButton variant="outlined" color="secondary" size="small" startIcon={<Icon>tune</Icon>}
              onClick={() => setConfigOpen(true)}>
              Attributes
            </SoftButton>
            <SoftButton
              variant="gradient" color="primary" size="small"
              startIcon={saving ? <CircularProgress size={12} color="inherit" /> : <Icon>save</Icon>}
              onClick={handleSave}
              disabled={saving || !batchId || players.length === 0}
            >
              {saving ? "Saving…" : "Save Session"}
            </SoftButton>
          </SoftBox>
        </SoftBox>

        {/* Session selector */}
        <Card sx={{ mb: 2.5 }}>
          <SoftBox p={2} display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <TextField select size="small" label="Batch *" value={batchId}
              onChange={(e) => { setBatchId(e.target.value); setSaved(false); setSelectedPlayer(null); }} sx={{ minWidth: 220 }}>
              <MenuItem value=""><em>Select a batch…</em></MenuItem>
              {batches.map((b) => <MenuItem key={b.id} value={b.id}>{b.name} ({b.ageGroup})</MenuItem>)}
            </TextField>
            <TextField size="small" label="Session Date" type="date" value={sessionDate}
              onChange={(e) => { setSessionDate(e.target.value); setSaved(false); }}
              InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }} />

            {batchId && players.length > 0 && (
              <SoftBox display="flex" gap={2.5} ml="auto" alignItems="center" flexWrap="wrap">
                {[
                  { label: "Players", val: players.length, color: "#1565c0" },
                  { label: "Rated",   val: `${ratedCount}/${players.length}`, color: ratedCount === players.length ? "#2e7d32" : "#f57c00" },
                  { label: "Present", val: presentCount, color: "#2e7d32" },
                  batchAvg > 0 && { label: "Avg", val: `${batchAvg.toFixed(1)} ${getSmiley(Math.round(batchAvg))?.emoji ?? ""}`, color: "#9c27b0" },
                ].filter(Boolean).map(({ label, val, color }) => (
                  <SoftBox key={label} textAlign="center">
                    <SoftTypography variant="caption" color="text" display="block" sx={{ fontSize: "0.6rem" }}>{label}</SoftTypography>
                    <SoftTypography fontWeight="bold" sx={{ fontSize: "0.95rem", color }}>{val}</SoftTypography>
                  </SoftBox>
                ))}
              </SoftBox>
            )}
          </SoftBox>
        </Card>

        {/* Player list */}
        {!batchId ? (
          <SoftBox py={8} display="flex" flexDirection="column" alignItems="center" gap={2}
            sx={{ backgroundColor: "#f8f9fb", borderRadius: 3, border: "2px dashed #e0e0e0" }}>
            <Icon sx={{ fontSize: "3.5rem !important", color: "#ccc" }}>sports_soccer</Icon>
            <SoftTypography variant="h6" color="text">Select a batch to begin</SoftTypography>
            <SoftTypography variant="caption" color="text" sx={{ maxWidth: 260, textAlign: "center", opacity: 0.7 }}>
              Choose a batch and date above, then tap any player to open their rating card
            </SoftTypography>
          </SoftBox>
        ) : playersLoading ? (
          <FootballLoader label="Loading players…" />
        ) : players.length === 0 ? (
          <SoftBox py={6} textAlign="center">
            <Icon sx={{ fontSize: "2.5rem !important", color: "#ccc" }}>person_off</Icon>
            <SoftTypography variant="h6" color="text" display="block">No active players in this batch</SoftTypography>
          </SoftBox>
        ) : (
          <SoftBox>
            {players.map((player) => (
              <PlayerRow
                key={player.id}
                player={player}
                rating={ratings[player.id]}
                attributes={attributes}
                onSelect={() => setSelectedPlayer(player)}
                onToggleAttendance={() => toggleAttendance(player.id)}
              />
            ))}
          </SoftBox>
        )}

        {/* Sticky save bar */}
        {batchId && players.length > 0 && (
          <SoftBox
            mt={2} p={1.5} display="flex" justifyContent="space-between" alignItems="center"
            sx={{ backgroundColor: "#fff", borderRadius: 2, boxShadow: "0 -2px 12px rgba(0,0,0,0.06)", position: "sticky", bottom: 16 }}
          >
            <SoftTypography variant="caption" color="text">
              <strong>{ratedCount}/{players.length}</strong> rated · <strong>{presentCount}</strong> present
            </SoftTypography>
            <SoftButton
              variant="gradient" color="primary"
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Icon>save</Icon>}
              onClick={handleSave} disabled={saving}
            >
              {saving ? "Saving…" : "Save Session"}
            </SoftButton>
          </SoftBox>
        )}
      </SoftBox>

      <Footer company={{ href: "#", name: "Mohammedan SC" }} links={[]} />

      {/* Player rating modal */}
      <PlayerRatingModal
        open={!!selectedPlayer}
        onClose={() => setSelectedPlayer(null)}
        player={selectedPlayer}
        rating={selectedPlayer ? ratings[selectedPlayer.id] : null}
        attributes={attributes}
        onChange={(attrId, v) => selectedPlayer && updateRating(selectedPlayer.id, attrId, v)}
        onOverallChange={(v) => selectedPlayer && updateOverall(selectedPlayer.id, v)}
        onToggleAttendance={() => selectedPlayer && toggleAttendance(selectedPlayer.id)}
        onSave={async () => { await handleSave(); setSelectedPlayer(null); }}
        saving={saving}
      />

      <AttributeConfigDialog
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        attributes={attributes}
        onSave={handleSaveAttributes}
      />

      <Snackbar open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.sev} onClose={() => setSnack((s) => ({ ...s, open: false }))} sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

function AcademyRatings() {
  return COMING_SOON ? <ComingSoonPage /> : <AcademyRatingsPage />;
}

export default AcademyRatings;
